import { useState, useEffect } from "react";
import { JobOffersAddWizard } from "@/components/jobs/JobOffersAddWizard";
import { JobCreationMethodModal } from "@/components/jobs/JobCreationMethodModal";
import { JobDescriptionPasteModal } from "@/components/jobs/JobDescriptionPasteModal";
import { JobDescriptionUploadModal } from "@/components/jobs/JobDescriptionUploadModal";
import { ProcessingOverlay } from "@/components/ui/ProcessingOverlay";
import { ModalForm } from "@/components/ModalForm";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";

interface JobFormData {
  title: string;
  companyId: string;
  companyName: string;
  description: string;
  skills: string;
  contactId?: string;
  aiSummary: string;
  
  // Fields
  employmentType: string;
  timeCommitment: string;
  contractDuration: string;
  startDate: string;
  seniorityLevel: string;
  budget: string;
  hiringManager: string;
  locationType: string;
  workplaceBreakdown: string;
  hardSkills: string;
  softSkills: string;
  educationLevel: string;
  yearsOfExperience: string;
  languageRequirements: string;
  cultureTags: string;
  applicationDeadline: string;
  jobPostingDate: string;
  source: string;
  assignedRecruiter: string;
  _job_languages?: Array<{
    language: string;
    language_code: string;
    min_cefr_level: number;
    is_mandatory: boolean;
    is_user_owned: boolean;
  }>;
}

interface CompanyInfo {
  id: string;
  name: string;
}

interface JobOffersAddModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddJob: (jobData: any) => Promise<string | undefined>;
  initialCompany?: CompanyInfo;
}

type CreationStep = 'method' | 'upload' | 'paste' | 'form';

export const JobOffersAddModal = ({ isOpen, onClose, onAddJob, initialCompany }: JobOffersAddModalProps) => {
  const [currentStep, setCurrentStep] = useState<CreationStep>('method');
  const [formData, setFormData] = useState<Partial<JobFormData>>({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [processingStage, setProcessingStage] = useState('');
  const { t } = useTranslation();
  
  // Initialize company data from initialCompany when provided
  useEffect(() => {
    if (initialCompany && isOpen) {
      // Set the company info in the form data
      setFormData(prevData => ({
        ...prevData,
        companyId: initialCompany.id,
        companyName: initialCompany.name
      }));
    }
  }, [initialCompany, isOpen]);
  
  const handleAddJob = async (jobData: any) => {
    // Process the new job data to match the expected format from the parent
    //console.log("JobOffersAddModal handleAddJob called with data:", jobData);
    
    try {
      // Ensure required fields are present (check both snake_case and camelCase for compatibility)
      const companyId = jobData.company_id || jobData.companyId;
      if (!jobData.title || !companyId) {
        console.error("Missing required fields:", { title: jobData.title, companyId });
        throw new Error("Title and company are required fields");
      }

      // Extract job languages before building formattedData (not a DB column)
      const jobLanguages = jobData._job_languages || [];

      // Format the data to match the expected structure
      const formattedData = {
        ...jobData,
        _job_languages: undefined, // strip internal field before passing to parent
        // Ensure status is always set
        status: (jobData.status && String(jobData.status).trim()) || "open",
        // Convert string skills to array if needed
        skills: typeof jobData.skills === 'string' 
          ? jobData.skills.split(',').map((skill: string) => skill.trim()).filter(Boolean)
          : Array.isArray(jobData.skills) 
            ? jobData.skills.filter(Boolean)
            : [],
        // Convert string arrays to actual arrays (use snake_case to match database)
        hard_skills: typeof jobData.hard_skills === 'string'
          ? jobData.hard_skills.split(',').map((skill: string) => skill.trim()).filter(Boolean)
          : Array.isArray(jobData.hard_skills)
            ? jobData.hard_skills.filter(Boolean)
            : [],
        soft_skills: typeof jobData.soft_skills === 'string'
          ? jobData.soft_skills.split(',').map((skill: string) => skill.trim()).filter(Boolean)
          : Array.isArray(jobData.soft_skills)
            ? jobData.soft_skills.filter(Boolean)
            : [],
        culture_tags: typeof jobData.culture_tags === 'string'
          ? jobData.culture_tags.split(',').map((tag: string) => tag.trim()).filter(Boolean)
          : Array.isArray(jobData.culture_tags)
            ? jobData.culture_tags.filter(Boolean)
            : [],
      };

      // Call the parent handler with the processed data
      const result = await onAddJob(formattedData);
      
      if (result) {
        // Insert job languages now that we have the job ID
        if (jobLanguages.length > 0) {
          await supabase.from('job_languages').insert(
            jobLanguages.map((lang: any) => ({
              job_id: result,
              language: lang.language,
              language_code: lang.language_code,
              min_cefr_level: lang.min_cefr_level,
              is_mandatory: lang.is_mandatory,
              is_user_owned: lang.is_user_owned
            }))
          );
        }
        resetForm();
        onClose();
        return result;
      } else {
        console.error("Job creation failed - no ID returned");
        throw new Error("Job creation failed - no ID returned");
      }
    } catch (error) {
      console.error("Error in job creation:", error);
      throw error;
    }
  };

  const resetForm = () => {
    setFormData({});
    setCurrentStep('method');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleMethodSelect = (method: 'upload' | 'paste' | 'manual', file?: File) => {
    //console.log("Method selected:", method);
    switch (method) {
      case 'upload':
        setCurrentStep('upload');
        break;
      case 'paste':
        setCurrentStep('paste');
        break;
      case 'manual':
        // If we have initialCompany, skip the method selection and go straight to form
        setCurrentStep('form');
        break;
    }
  };

  const handleProcessedData = (extractedData: any) => {
    //console.log('Extracted Data from AI processing:', extractedData);

    // Map the extracted data to our form data structure
    const mappedData: Partial<JobFormData> = {
      title: extractedData.title || '',
      description: extractedData.description || '',
      skills: Array.isArray(extractedData.skills) 
        ? extractedData.skills.join(', ') 
        : extractedData.skills || '',
      employmentType: extractedData.employment_type || '',
      timeCommitment: extractedData.time_commitment || '',
      contractDuration: extractedData.contract_duration || '',
      seniorityLevel: extractedData.seniority_level || '',
      locationType: extractedData.location_type || '',
      workplaceBreakdown: extractedData.workplace_breakdown || '',
      hardSkills: Array.isArray(extractedData.hard_skills) 
        ? extractedData.hard_skills.join(', ') 
        : extractedData.hard_skills || '',
      softSkills: Array.isArray(extractedData.soft_skills) 
        ? extractedData.soft_skills.join(', ') 
        : extractedData.soft_skills || '',
      educationLevel: extractedData.education_level || '',
      yearsOfExperience: extractedData.years_of_experience || '',
      languageRequirements: '',
      _job_languages: Array.isArray(extractedData.language_requirements)
        ? extractedData.language_requirements.map((lang: any) => ({
            language: lang.language,
            language_code: lang.language_code,
            min_cefr_level: lang.min_cefr_level ?? 0,
            is_mandatory: lang.is_mandatory ?? true,
            is_user_owned: false
          }))
        : [],
      cultureTags: Array.isArray(extractedData.culture_tags) 
        ? extractedData.culture_tags.join(', ') 
        : extractedData.culture_tags || '',
      aiSummary: extractedData.summary || extractedData.ai_summary || '',
      // Explicitly add status field
      status: extractedData.status || 'open',
    } as any;
    
    // Add company info if available
    if (initialCompany) {
      mappedData.companyId = initialCompany.id;
      mappedData.companyName = initialCompany.name;
    }
    
    //console.log('Mapped data for form:', mappedData);
    setFormData(mappedData);
    setCurrentStep('form');
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    // Don't submit if formData is empty (wizard flow handles its own submission)
    if (!formData || Object.keys(formData).length === 0 || !formData.title) {
      return;
    }
    
    setIsProcessing(true);
    setProcessingStage("Processing...");

    try {
      const result = await handleAddJob(formData);
      if (result) {
        //console.log("Job created successfully with ID:", result);
        resetForm();
      } else {
        console.error("Job creation failed - no ID returned");
      }
    } catch (error) {
      console.error("Error in job creation:", error);
    } finally {
      setIsProcessing(false);
      setProcessingStage("Job created successfully!");
    }
  };

  // Render the appropriate component based on the current step
  if (!isOpen) return null;

  // For the wizard form mode, render the wizard directly without ModalForm wrapper
  if (currentStep === 'form') {
    return (
      <JobOffersAddWizard 
        isOpen={isOpen}
        onClose={handleClose}
        onAddJob={handleAddJob}
        initialJobData={formData}
      />
    );
  }

  // For method selection, upload, and paste modes, use the ModalForm wrapper
  return (
    <ModalForm
      title={t("jobs.addJobModal.title")}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitText={isProcessing ? t("common.loading") : t("jobs.addJobModal.saveButton")}
      submitDisabled={isProcessing}
      cancelText={t("jobs.addJobModal.cancelButton")}
    >
      <ProcessingOverlay 
        isProcessing={isProcessing}
        progress={progress}
        status={processingStage}
        message={t("jobs.jobOfferModal.creatingJobOffer")}
      />
      <JobCreationMethodModal 
        isOpen={isOpen && currentStep === 'method'}
        onClose={handleClose}
        onSelectMethod={handleMethodSelect}
      />
      
      <JobDescriptionUploadModal 
        isOpen={isOpen && currentStep === 'upload'}
        onClose={() => setCurrentStep('method')}
        onProcessed={handleProcessedData}
      />
      
      <JobDescriptionPasteModal 
        isOpen={isOpen && currentStep === 'paste'}
        onClose={() => setCurrentStep('method')}
        onProcessed={handleProcessedData}
      />
    </ModalForm>
  );
};
